import { useMemo } from 'react';
import * as Icons from '@mui/icons-material';

/**
 * Hook to filter recipes based on various criteria and build recipe menu options
 * 
 * @param {Array} recipes - Array of recipe objects
 * @param {Array} inventory - Array of inventory ingredient objects
 * @param {Set} favorites - Set of favorite recipe IDs
 * @param {Set} bookmarks - Set of bookmarked recipe IDs
 * @param {Object} activeIngredientSet - Optional active ingredient set to use instead of inventory
 * @returns {Object} - Object containing filtered recipe lists and menu options
 */
const useRecipeFilters = (recipes, inventory, favorites, bookmarks, activeIngredientSet = null) => {
  // Create a cached inventory map for faster lookup
  const inventoryMap = useMemo(() => {
    const map = new Map();
    
    // If we have an active ingredient set, use its ingredients
    if (activeIngredientSet && activeIngredientSet.ingredients) {
      // With a set, possessed is determined by presence in the set
      activeIngredientSet.ingredients.forEach(ingredientId => {
        map.set(ingredientId, true);
      });
    } else {
      // Without a set, use the regular inventory with possessed status
      inventory.forEach(ing => {
        if (ing.possessed) {
          map.set(ing.id, true);
        }
      });
    }
    
    return map;
  }, [inventory, activeIngredientSet]);
  
  // Filter recipes that user can make with current inventory
  const canMakeRecipes = useMemo(() => {
    return recipes.filter(recipe => {
      const requiredIngredients = recipe.ingredients || [];
      // Use ingredientId consistently
      const requiredIds = new Set(requiredIngredients.map(ing => ing.ingredientId || ing.id));
      
      // Check if all required ingredients are in user inventory using the map
      return [...requiredIds].every(id => inventoryMap.has(id));
    });
  }, [recipes, inventoryMap]);

  // Create a function to count missing ingredients
  const countMissingIngredients = useMemo(() => {
    return (recipe) => {
      const requiredIngredients = recipe.ingredients || [];
      const requiredIds = new Set(requiredIngredients.map(ing => ing.ingredientId || ing.id));
      
      // Count missing ingredients using the map
      let missingCount = 0;
      for (const id of requiredIds) {
        if (!inventoryMap.has(id)) {
          missingCount++;
        }
      }
      return missingCount;
    };
  }, [inventoryMap]);

  // Filter recipes missing exactly one ingredient
  const missingOneRecipes = useMemo(() => {
    return recipes.filter(recipe => countMissingIngredients(recipe) === 1);
  }, [recipes, countMissingIngredients]);

  // Filter recipes missing exactly two ingredients
  const missingTwoRecipes = useMemo(() => {
    return recipes.filter(recipe => countMissingIngredients(recipe) === 2);
  }, [recipes, countMissingIngredients]);

  // Filter favorite recipes
  const favoriteRecipes = useMemo(() => {
    return recipes.filter(recipe => favorites?.has(recipe.id));
  }, [recipes, favorites]);

  // Filter bookmarked recipes
  const bookmarkedRecipes = useMemo(() => {
    return recipes.filter(recipe => bookmarks?.has(recipe.id));
  }, [recipes, bookmarks]);

  // Build recipe menu options
  const recipeMenus = useMemo(() => [
    // Top row: All Recipes, Favorites, Bookmarks
    {
      name: 'All Recipes',
      count: recipes.length,
      path: '/recipes/all',
      icon: <Icons.LocalBar />
    },
    {
      name: 'Favorites',
      count: favoriteRecipes.length,
      path: '/recipes/favorites',
      icon: <Icons.Favorite />
    },
    {
      name: 'Bookmarks',
      count: bookmarkedRecipes.length,
      path: '/recipes/bookmarks',
      icon: <Icons.Bookmark />
    },
    
    // Bottom row: Can Make, Missing 1, Missing 2
    {
      name: 'Recipes I Can Make',
      count: canMakeRecipes.length,
      path: '/recipes/can-make',
      icon: <Icons.CheckCircle />
    },
    {
      name: 'Missing 1 Ingredient',
      count: missingOneRecipes.length,
      path: '/recipes/missing-one',
      icon: <Icons.LooksOne />
    },
    {
      name: 'Missing 2 Ingredients',
      count: missingTwoRecipes.length,
      path: '/recipes/missing-two',
      icon: <Icons.LooksTwo />
    }
  ], [
    recipes, 
    canMakeRecipes, 
    missingOneRecipes, 
    missingTwoRecipes, 
    favoriteRecipes,
    bookmarkedRecipes
  ]);

  return {
    canMakeRecipes,
    missingOneRecipes,
    missingTwoRecipes,
    favoriteRecipes,
    bookmarkedRecipes,
    recipeMenus
  };
};

export default useRecipeFilters; 